import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { Subscription } from 'rxjs';
import { MenuItem, MenuItemChild, SIDEBAR_MENU_ITEMS, SIDEBAR_ICONS } from './sidebar.constants';
import { AuthService } from '../../../core/services/auth.service';
import { RbacService } from '../../../core/rbac/rbac.service';
import { AppRole } from '../../../core/rbac/roles.constants';
import { getPermissionsForRoute } from '../../../core/rbac/menu-permissions.config';

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css'
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() menuItems: MenuItem[] = SIDEBAR_MENU_ITEMS;
  @Output() openSidebar = new EventEmitter<void>();

  /** Filtered menu items by RBAC and legacy per-role submenu rules. */
  displayMenuItems: MenuItem[] = [];
  currentRoute: string = '';
  /** Target path as soon as navigation starts (lazy routes delay NavigationEnd). */
  private pendingRoutePath: string = '';
  expandedItems: Set<string> = new Set();
  /** For 3-level menus: expanded second-level sections (e.g. "Batch Admin", "Charges") keyed by "parentLabel-childLabel" */
  expandedSubItems: Set<string> = new Set();
  /** For 4-level menus: expanded third-level (e.g. "Diagnosis Procedure" under Plan Reports) keyed by "parent-child-grandchild" */
  expandedSubSubItems: Set<string> = new Set();
  private routerSubscription?: Subscription;
  private authSubscription?: Subscription;

  chevronDown = SIDEBAR_ICONS.chevronDown;
  chevronRight = SIDEBAR_ICONS.chevronRight;

  constructor(
    private router: Router,
    private authService: AuthService,
    private rbac: RbacService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.updateDisplayMenuItems();

    this.authSubscription = this.authService.currentUser$.subscribe(() => {
      this.updateDisplayMenuItems();
      this.cdr.detectChanges();
    });

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.pendingRoutePath = (event.url || '').split('?')[0] || '';
        this.autoExpandActiveMenu();
        this.cdr.markForCheck();
      } else if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects || event.url;
        this.pendingRoutePath = '';
        this.autoExpandActiveMenu();
        this.cdr.markForCheck();
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.pendingRoutePath = '';
        this.cdr.markForCheck();
      }
    });

    this.autoExpandActiveMenu();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
  }

  /** Filter menu items by RBAC: permission-based visibility; Administrator sees full menu. */
  private updateDisplayMenuItems(): void {
    if (this.rbac.isAdministrator()) {
      this.displayMenuItems = this.menuItems;
      return;
    }
    this.displayMenuItems = this.menuItems
      .map((item) => this.filterMenuItemByPermission(item))
      .filter((item) => this.hasVisibleContent(item));
  }

  private filterMenuItemByPermission(item: MenuItem): MenuItem {
    if (!item.children?.length) {
      return item;
    }
    let childrenToFilter = item.children;
    // For Batch menu: Doctor role sees only "Batch Admin" and "Charges"; Biller/Admin see all.
    if (item.label === 'Batch') {
      const user = this.authService.getCurrentUser();
      const isDoctorOnly = user?.userTypes?.doctor === true && user?.userTypes?.biller !== true;
      const isFrontDeskOnly =
        user?.userTypes?.frontDesk === true &&
        user?.userTypes?.doctor !== true &&
        user?.userTypes?.nurse !== true &&
        user?.userTypes?.biller !== true;
      const isBillerOnly =
        this.rbac.hasRole(AppRole.BILLER) &&
        user?.userTypes?.doctor !== true &&
        user?.userTypes?.frontDesk !== true &&
        user?.userTypes?.nurse !== true;

      // FrontDesk requirement: show only the specific Batch submenus/routes from legacy UI.
      if (isFrontDeskOnly) {
        const batchAdmin = item.children.find((c) => c.label === 'Batch Admin');
        const charges = item.children.find((c) => c.label === 'Charges');
        const payment = item.children.find((c) => c.label === 'Payment');
        const printing = item.children.find((c) => c.label === 'Printing');
        const viewer = item.children.find((c) => c.label === 'Viewer');

        const chargeBatchAdmin = batchAdmin?.children?.find((c) => c.label === 'Charge Batch Admin');

        const chargeEntry = charges?.children?.find((c) => c.label === 'Charge Entry');
        const chargeListing = charges?.children?.find((c) => c.label === 'Charge Listing');

        const paymentLink = payment?.children?.find((c) => c.route === '/batch/payment-management');

        const statementPrinting = printing?.children?.find((c) => c.label === 'Statement Printing');
        const paymentDocumentViewer = viewer?.children?.find((c) => c.label === 'Payment Document Viewer');

        childrenToFilter = [
          ...(chargeBatchAdmin ? [{ label: 'Batch Admin', children: [chargeBatchAdmin] } as MenuItemChild] : []),
          ...((chargeEntry || chargeListing)
            ? [
                {
                  label: 'Charges',
                  children: [chargeEntry, chargeListing].filter(Boolean) as MenuItemChild[]
                } as MenuItemChild
              ]
            : []),
          ...(paymentLink ? [{ label: 'Payment', children: [paymentLink] } as MenuItemChild] : []),
          ...(statementPrinting ? [{ label: 'Printing', children: [statementPrinting] } as MenuItemChild] : []),
          ...(paymentDocumentViewer ? [{ label: 'Viewer', children: [paymentDocumentViewer] } as MenuItemChild] : []),
        ];
      } else if (isDoctorOnly) {
        // Doctor-only requirement: show only two submenu links:
        // - Batch Admin -> Charge Batch Admin
        // - Charges -> Charge Listing
        const batchAdmin = item.children.find((c) => c.label === 'Batch Admin');
        const charges = item.children.find((c) => c.label === 'Charges');

        const chargeBatchAdmin = batchAdmin?.children?.find((c) => c.label === 'Charge Batch Admin');
        const chargeListing = charges?.children?.find((c) => c.label === 'Charge Listing');

        childrenToFilter = [
          ...(chargeBatchAdmin ? [{ label: 'Batch Admin', children: [chargeBatchAdmin] } as MenuItemChild] : []),
          ...(chargeListing ? [{ label: 'Charges', children: [chargeListing] } as MenuItemChild] : []),
        ];
      } else if (isBillerOnly) {
        // Old site requirement: hide extra Batch sections for BILLER.
        childrenToFilter = item.children.filter((c) =>
          ['Batch Admin', 'Charges', 'Payment', 'Printing', 'Viewer'].includes(c.label)
        );
      }
    }
    // For Reports menu: Nurse-only should see only the three report items from legacy UI.
    if (item.label === 'Reports') {
      const user = this.authService.getCurrentUser();
      const isNurseOnly =
        user?.userTypes?.nurse === true &&
        user?.userTypes?.doctor !== true &&
        user?.userTypes?.frontDesk !== true &&
        user?.userTypes?.biller !== true;
      if (isNurseOnly) {
        childrenToFilter = item.children.filter((c) =>
          c.label === 'Patient Reports' || c.label === 'Schedule Reports'
        );
      } else {
        const isFrontDeskOnly =
          user?.userTypes?.frontDesk === true &&
          user?.userTypes?.doctor !== true &&
          user?.userTypes?.nurse !== true &&
          user?.userTypes?.biller !== true;

        // Old site requirement: FrontDesk should see only these report groups.
        if (isFrontDeskOnly && this.rbac.hasRole(AppRole.FRONT_DESK)) {
          const allowedReportLabels = new Set<string>([
            'Patient Reports',
            'Schedule Reports',
            'Plan Reports',
            'Follow Up Report',
          ]);
          childrenToFilter = item.children.filter((c) =>
            allowedReportLabels.has(c.label)
          );
        }

        const isDoctorOnly =
          user?.userTypes?.doctor === true &&
          user?.userTypes?.frontDesk !== true &&
          user?.userTypes?.nurse !== true &&
          user?.userTypes?.biller !== true;
        if (isDoctorOnly && this.rbac.hasRole(AppRole.DOCTOR)) {
          const allowedReportLabels = new Set<string>([
            'Patient Reports',
            'Management Reports',
            'Schedule Reports',
            'Plan Reports',
            'Follow Up Report',
          ]);
          childrenToFilter = item.children.filter((c) => allowedReportLabels.has(c.label));
        }
      }
    }

    // Old site requirement: Utility for Doctor should not include "Patient Recall Report" under it.
    if (item.label === 'Utility' && item.children?.length) {
      const user = this.authService.getCurrentUser();
      const isDoctorOnly =
        user?.userTypes?.doctor === true &&
        user?.userTypes?.frontDesk !== true &&
        user?.userTypes?.nurse !== true &&
        user?.userTypes?.biller !== true;
      if (isDoctorOnly && this.rbac.hasRole(AppRole.DOCTOR)) {
        childrenToFilter = item.children.filter((c) => c.label !== 'Patient Recall Report');
      }
    }

    // For Master menu: BILLER should see only a specific set of menu groups.
    // (Security -> Users/User Role is intentionally hidden.)
    if (item.label === 'Master' && this.rbac.hasRole(AppRole.BILLER)) {
      const allowedMasterLabels = new Set<string>([
        'Code Management',
        'Entity Management',
        'Others',
        'Insurance Management',
        'Follow-Up Profile',
        'Patient Messages',
        'To Do List',
        'Show System Options',
        'Appointment Color',
      ]);
      childrenToFilter = childrenToFilter.filter((c) => allowedMasterLabels.has(c.label));
    }

    // For Master menu: NURSE should see only the exact legacy groups you listed.
    // (No Security, no Insurance Management, etc.)
    if (item.label === 'Master') {
      const user = this.authService.getCurrentUser();
      const isNurseOnly =
        user?.userTypes?.nurse === true &&
        user?.userTypes?.doctor !== true &&
        user?.userTypes?.frontDesk !== true &&
        user?.userTypes?.biller !== true;

      if (isNurseOnly && this.rbac.hasRole(AppRole.NURSE)) {
        const allowedMasterLabels = new Set<string>([
          'Code Management',
          'Entity Management',
          'Others',
          'Follow-Up Profile',
          'Patient Messages',
          'To Do List',
          'Show System Options',
          'Appointment Color',
        ]);
        childrenToFilter = childrenToFilter.filter((c) => allowedMasterLabels.has(c.label));
      }

      // Old site requirement: FRONT DESK master menu should NOT show Security/Insurance.
      const isFrontDeskOnly =
        user?.userTypes?.frontDesk === true &&
        user?.userTypes?.doctor !== true &&
        user?.userTypes?.nurse !== true &&
        user?.userTypes?.biller !== true;
      if (isFrontDeskOnly && this.rbac.hasRole(AppRole.FRONT_DESK)) {
        const allowedMasterLabels = new Set<string>([
          'Code Management',
          'Entity Management',
          'Others',
          'Follow-Up Profile',
          'Patient Messages',
          'To Do List',
          'Show System Options',
          'Appointment Color',
        ]);
        childrenToFilter = childrenToFilter.filter((c) =>
          allowedMasterLabels.has(c.label)
        );
      }

      const isDoctorOnly =
        user?.userTypes?.doctor === true &&
        user?.userTypes?.frontDesk !== true &&
        user?.userTypes?.nurse !== true &&
        user?.userTypes?.biller !== true;

      // Old site requirement: Doctor Master menu shows ONLY these groups.
      if (isDoctorOnly && this.rbac.hasRole(AppRole.DOCTOR)) {
        const allowedMasterLabels = new Set<string>([
          'Patient Messages',
          'To Do List',
          'Show System Options',
          'Appointment Color',
        ]);
        childrenToFilter = childrenToFilter.filter((c) => allowedMasterLabels.has(c.label));
      }
    }

    const filteredChildren = childrenToFilter
      .map((child) => this.filterChildByPermission(child))
      .filter((child) => this.childHasVisibleContent(child));
    return { ...item, children: filteredChildren };
  }

  private filterChildByPermission(child: MenuItemChild): MenuItemChild {
    if (!child.children?.length) {
      return child;
    }
    const filteredGrand = child.children
      .map((gc) => this.filterGrandchildByPermission(gc))
      .filter((gc) => this.grandchildHasVisibleContent(gc));
    return { ...child, children: filteredGrand };
  }

  private filterGrandchildByPermission(gc: MenuItemChild): MenuItemChild {
    if (!gc.children?.length) return gc;
    const filtered = gc.children.filter((ggc) => this.isRouteVisible(ggc.route));
    return { ...gc, children: filtered };
  }

  private isRouteVisible(route: string | undefined): boolean {
    if (!route) return true;
    const perms = getPermissionsForRoute(route);
    if (perms.length === 0) return true;
    if (this.rbac.canAny(perms)) return true;
    // Show Batch menu and all batch routes to users with doctor role (userType)
    if (route === '/batch' || route.startsWith('/batch/')) {
      const user = this.authService.getCurrentUser();
      if (user?.userTypes?.doctor === true) return true;
    }
    return false;
  }

  private hasVisibleContent(item: MenuItem): boolean {
    if (item.children?.length) {
      return item.children.some((child) => this.childHasVisibleContent(child));
    }
    if (item.route) return this.isRouteVisible(item.route);
    return false;
  }

  private childHasVisibleContent(child: MenuItemChild): boolean {
    if (child.route) return this.isRouteVisible(child.route);
    return !!child.children?.length;
  }

  private grandchildHasVisibleContent(gc: MenuItemChild): boolean {
    if (gc.route) return this.isRouteVisible(gc.route);
    return !!gc.children?.length;
  }

  private autoExpandActiveMenu(): void {
    const items = this.displayMenuItems.length > 0 ? this.displayMenuItems : this.menuItems;
    items.forEach(item => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(child =>
          this.hasActiveInChild(item.label, child)
        );
        if (hasActiveChild) {
          this.expandedItems.add(item.label);
        }
        if (item.accordionChildren && hasActiveChild) {
          const activeChild = item.children.find(child => this.hasActiveInChild(item.label, child));
          if (activeChild) {
            item.children.forEach(c => {
              if (c !== activeChild) this.expandedSubItems.delete(this.subItemKey(item.label, c.label));
            });
            this.expandedSubItems.add(this.subItemKey(item.label, activeChild.label));
          }
        } else {
          item.children.forEach(child => {
            if (child.children && child.children.length > 0) {
              const key = this.subItemKey(item.label, child.label);
              const activeInGrandchild = child.children.some(gc =>
                (gc.route && this.isRouteActive(gc.route)) ||
                (gc.children && gc.children.some(ggc => ggc.route && this.isRouteActive(ggc.route)))
              );
              if (activeInGrandchild) this.expandedSubItems.add(key);
              child.children.forEach(gc => {
                if (gc.children && gc.children.length > 0) {
                  const subKey = this.subSubItemKey(item.label, child.label, gc.label);
                  const activeInGreatGrandchild = gc.children.some(ggc => ggc.route && this.isRouteActive(ggc.route));
                  if (activeInGreatGrandchild) this.expandedSubSubItems.add(subKey);
                }
              });
            }
          });
        }
      }
    });
  }

  private hasActiveInChild(parentLabel: string, child: MenuItemChild): boolean {
    if (child.route && this.isRouteActive(child.route)) return true;
    if (child.children && child.children.length > 0) {
      return child.children.some((gc: MenuItemChild) =>
        (!!gc.route && this.isRouteActive(gc.route)) ||
        (gc.children && gc.children.some(ggc => !!ggc.route && this.isRouteActive(ggc.route)))
      );
    }
    return false;
  }

  private subItemKey(parentLabel: string, childLabel: string): string {
    return `${parentLabel}-${childLabel}`;
  }

  private subSubItemKey(parentLabel: string, childLabel: string, grandchildLabel: string): string {
    return `${parentLabel}-${childLabel}-${grandchildLabel}`;
  }

  hasSubSubChildren(grandchild: { children?: unknown[] }): boolean {
    return !!(grandchild.children && grandchild.children.length > 0);
  }

  isSubSubExpanded(item: MenuItem, child: MenuItemChild, grandchild: MenuItemChild): boolean {
    return this.expandedSubSubItems.has(this.subSubItemKey(item.label, child.label, grandchild.label));
  }

  toggleSubSubExpand(item: MenuItem, child: MenuItemChild, grandchild: MenuItemChild, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const key = this.subSubItemKey(item.label, child.label, grandchild.label);
    if (this.expandedSubSubItems.has(key)) {
      this.expandedSubSubItems.delete(key);
    } else {
      this.expandedSubSubItems.add(key);
    }
  }

  isGreatGrandchildActive(greatGrandchild: MenuItemChild): boolean {
    return !!(greatGrandchild.route && this.isRouteActive(greatGrandchild.route));
  }

  hasSubChildren(child: { children?: unknown[] }): boolean {
    return !!(child.children && child.children.length > 0);
  }

  isSubExpanded(item: MenuItem, child: MenuItemChild): boolean {
    return this.expandedSubItems.has(this.subItemKey(item.label, child.label));
  }

  toggleSubExpand(item: MenuItem, child: MenuItemChild, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const key = this.subItemKey(item.label, child.label);
    if (this.expandedSubItems.has(key)) {
      this.expandedSubItems.delete(key);
    } else {
      if (item.accordionChildren && item.children) {
        item.children.forEach(c => {
          if (c !== child) this.expandedSubItems.delete(this.subItemKey(item.label, c.label));
        });
      }
      this.expandedSubItems.add(key);
    }
  }

  /** Path used for active menu styling: pending target immediately on click, then committed URL. */
  private get activeRoutePath(): string {
    return this.pendingRoutePath || this.currentRoute.split('?')[0];
  }

  isMenuItemActive(item: MenuItem): boolean {
    if (!item.route) {
      return false;
    }

    const routePath = this.activeRoutePath;

    if (item.children && item.children.length > 0) {
      const hasActiveChild = item.children.some(child => this.hasActiveInChild(item.label, child));
      if (hasActiveChild) {
        return false;
      }
      if (routePath.startsWith(item.route + '/')) {
        return true;
      }
    }

    if (item.alwaysActive) {
      return this.isRouteActive(item.route);
    }

    if (!item.children || item.children.length === 0) {
      return routePath === item.route || routePath.startsWith(item.route + '/');
    }

    return routePath === item.route;
  }

  isChildActive(child: MenuItemChild): boolean {
    if (child.route) return this.isRouteActive(child.route);
    if (child.children && child.children.length > 0) {
      return child.children.some(gc =>
        (gc.route && this.isRouteActive(gc.route)) ||
        (gc.children && gc.children.some(ggc => ggc.route && this.isRouteActive(ggc.route)))
      );
    }
    return false;
  }

  isGrandchildWithChildrenActive(grandchild: MenuItemChild): boolean {
    if (grandchild.children && grandchild.children.length > 0) {
      return grandchild.children.some(ggc => ggc.route && this.isRouteActive(ggc.route));
    }
    return false;
  }

  isGrandchildActive(grandchild: MenuItemChild): boolean {
    return !!(grandchild.route && this.isRouteActive(grandchild.route));
  }

  private isRouteActive(route: string): boolean {
    const routePath = this.activeRoutePath;
    
    if (routePath === route) {
      return true;
    }

    if (routePath.startsWith(route + '/')) {
      return true;
    }

    return false;
  }

  toggleExpand(item: MenuItem): void {
    if (!item.children || item.children.length === 0) {
      return;
    }

    if (this.expandedItems.has(item.label)) {
      this.expandedItems.delete(item.label);
    } else {
      this.expandedItems.add(item.label);
    }
  }

  isExpanded(item: MenuItem): boolean {
    return this.expandedItems.has(item.label);
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  onParentClick(item: MenuItem, event: Event): void {
    if (!this.isOpen && this.hasChildren(item)) {
      event.preventDefault();
      this.openSidebar.emit();
      setTimeout(() => {
        if (!this.isExpanded(item)) {
          this.expandedItems.add(item.label);
        }
      }, 300);
      return;
    }

    if (this.hasChildren(item)) {
      event.preventDefault();
      this.toggleExpand(item);
    } else if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  onChildClick(child: { route: string }): void {
    this.router.navigate([child.route]);
  }

  closeSidebarOnMobile(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
    }
  }
}
